import Buttons, { Btn } from "../../../../components/Buttons/Buttons";
import { ReactComponent as PlusIcon } from '../../../../assets/Icons/adornments/Plus.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/Icons/adornments/Close.svg';
import { useState, MouseEvent, ChangeEvent, useEffect } from "react";
import ModalBase from "../../../../components/Popups/ModalBase";
import PopupHeader from "../../../../components/Popups/PopupHeader";
import PopupFooter from "../../../../components/Popups/PopupFooter";
import { DatasetVisualizationDAGRequestModel, DatasetVisualizationDAGRequestModelFpType, VisualizationMethodSpecification, VisualizationMethodSpecificationMethodType } from "../../../../api/models";
import SuccessMessageModal from "../../../../components/Popups/SuccessMessageModal";
import SelectField from "../../../../components/Form/SelectFields";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import InputField from "../../../../components/Form/InputField";
import BooleanField from "../../../../components/Form/BooleanField";
import usePost from "../../../../hooks/endpoints/usePost";

const fpOptions = [
    { value: 'ecfp6_1024', label: 'ECFP' },
    { value: 'fcfp6_1024', label: 'FCFP' },
    { value: 'maccs', label: 'MACCS' },
];

const visMethodOptions = [
    { value: 'tsne', label: 'TSNE' },
    { value: 'umap', label: 'UMAP' },
    { value: 'pca', label: 'PCA' }
]

const tsneOptions = [
    {value: 'barnes_hut', label: 'Barnes Hut'},
    {value: 'exact', label: 'Exact'},
];

const pcaOptions = [
    {value: 'auto', label: 'Auto'},
    {value: 'full', label: 'Full'},
    {value: 'covariance_eigh', label: 'Covariance Eigh'},
    {value: 'arpack', label: 'Arpack'},
    {value: 'randomized', label: 'Randomized'},
];

const umapMetricOptions = [
    {value: 'euclidean', label: 'Euclidean'},
    {value: 'manhattan', label: 'Manhattan'},
    {value: 'chebyshev', label: 'Chebyshev'},
    {value: 'minkowski', label: 'Minkowski'},
    {value: 'canberra', label: 'Canberra'},
    {value: 'braycurtis', label: 'Braycurtis'},
    {value: 'mahalanobis', label: 'Mahalanobis'},
    {value: 'wminkowski', label: 'Wminkowski'},
    {value: 'seuclidean', label: 'Seuclidean'},
    {value: 'cosine', label: 'Cosine'},
    {value: 'correlation', label: 'Correlation'},
    {value: 'haversine', label: 'Haversine'},
    {value: 'hamming', label: 'Hamming'},
    {value: 'jaccard', label: 'Jaccard'},
    {value: 'dice', label: 'Dice'},
    {value: 'russelrao', label: 'Russelrao'},
    {value: 'kulsinski', label: 'Kulsinski'},
    {value: 'll_dirichlet', label: 'LL Dirichlet'},
    {value: 'hellinger', label: 'Hellinger'},
    {value: 'rogerstanimoto', label: 'Rogerstanimoto'},
    {value: 'sokalmichener', label: 'Sokalmichener'},
    {value: 'sokalsneath', label: 'Sokalsneath'}, 
    {value: 'yule', label: 'Yule'}
];

const umapInitOptions = [
    {value: 'spectral', label: 'Spectral'},
    {value: 'random', label: 'Random'},
    {value: 'pca', label: 'PCA'},
    {value: 'tswspectral', label: 'TSW Spectral'},
]

const defaultForm: DatasetVisualizationDAGRequestModel = {
    fp_type: 'ecfp6_1024',
    visualization_method_specification_list: [{
        method_type: 'tsne',
        params: {
            perplexity: 30,
            method: 'barnes_hut'
        }
    }],
}

export default function GenerateVisualizationModal({
    setPopupMessage,
    resultsetId,
}: {
    setPopupMessage: (message: string, isSuccess: boolean) => void,
    resultsetId: number,
}) {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);

    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);
    const [ formBtns, setFormBtns ] = useState<Btn[]>([]);

    const [ form, setForm ] = useState<DatasetVisualizationDAGRequestModel>(defaultForm);

    const { handleGenerateVisualization } = usePost({
        setPopupMessage,
        setIsDisabled,
        setIsLoading,
        setIsSuccess
    });

    const handleClose = () => {
        setOpen(false);
        resetValues();
    };

    const resetValues = () => {
        setIsDisabled(false);
        setIsLoading(false);
        setIsSuccess(false);
        setShowSelect(false);
        setForm(defaultForm);
    }

    const handleSelectFp = (e:any, name: string) => {
        const value = e.value as DatasetVisualizationDAGRequestModelFpType;

        setForm({
            ...form,
            fp_type: value,
        });
    }

    const handleSelectVisMethod = (e:any, name: string, index?: number) => {
        const value = e.value as VisualizationMethodSpecificationMethodType;

        let visMethod: VisualizationMethodSpecification = {
            method_type: value,
        };

        if (value === 'tsne') {
            visMethod.params = {
                perplexity: 30,
                method: 'barnes_hut'
            }
        } else if (value === 'umap') {
            visMethod.params = {
                n_neighbors: 15,
                metric: 'euclidean',
                init: 'spectral',
            }
        } else {
            visMethod.params = {
                whiten: false,
                svd_solver: 'auto',
            }
        }

        if (index !== undefined) {
            setForm({
                ...form,
                visualization_method_specification_list: form.visualization_method_specification_list!.map((v, i) => {
                    if (i === index) {
                        return {...visMethod}
                    } else {
                        return {...v}
                    }
                })
            })
        }
    }

    const handleInput = (e:ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index?: number) => {
        const name = e.target.name;
        const value = e.target.value;

        if (index !== undefined) {
            setForm({
                ...form,
                visualization_method_specification_list: form.visualization_method_specification_list!.map((v,i) => {
                    if (i === index) {
                        return {...v, params: {...v.params, [name]: Number(value)}}
                    } else {
                        return {...v}
                    }
                })
            })
        }
    };

    const handleSelect = (e:any, name: string, index?: number) => {
        const value = e.value as string;

        if (index !== undefined) {
            setForm({
                ...form,
                visualization_method_specification_list: form.visualization_method_specification_list!.map((v,i) => {
                    if (i === index) {
                        return {...v, params: {...v.params, [name]: value}}
                    } else {
                        return {...v}
                    }
                })
            })
        }
    };

    const handleCheck = (e: ChangeEvent<HTMLInputElement>, index?: number) => {
        const checked = e.target.checked;
        const name = e.target.name;

        if (index !== undefined) {
            setForm({
                ...form,
                visualization_method_specification_list: form.visualization_method_specification_list!.map((v,i) => {
                    if (i === index) {
                        return {...v, params: {...v.params, [name]: checked}}
                    } else {
                        return {...v}
                    }
                })
            })
        }
    }

    const addVisMethod = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        setForm({
            ...form,
            visualization_method_specification_list: form.visualization_method_specification_list!.concat({
                method_type: 'tsne',
                params: {
                    perplexity: 30,
                    method: 'barnes_hut'
                }
            })
        })
    }

    const removeVisMethod = (e: MouseEvent<HTMLButtonElement>, index: number) => {
        e.preventDefault();

        setForm({
            ...form,
            visualization_method_specification_list: form.visualization_method_specification_list!.filter((v, i) => i !== index)
        });
    };

    useEffect(() => {
        setFormBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-vis-generation',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => {handleClose()},
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'generate-vis-btn',
                label: "Run",
                action: async (e: MouseEvent<HTMLButtonElement>) => {handleGenerateVisualization(e, form, resultsetId)},
                isLoading,
            }
        ])

        //eslint-disable-next-line
    }, [isDisabled, isLoading, form, resultsetId]);

    

    return (
        <div>
            <Buttons
                id='generate-vis-btn'
                type='primary'
                styling="solid"
                label="Generate Visualization"
                startingAdornment={<PlusIcon/>}
                action={handleOpen}
            />
            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="generate-vis-modal"
            >
                {!isSuccess ? (
                <div className={`bg-white rounded-lg max-w-[600px] w-full popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Generate Visualization"
                            hasOutline={true}
                            closeFn={handleClose}
                        />   
                        <div className="w-full flex flex-col gap-8 p-6 justify-start items-stretch overflow-y-auto">
                            
                            <div className="w-full">
                                <ConditionalSelectField label="Fingerprint" showSelect={showSelect}>
                                    <SelectField 
                                        label="Fingerprint"
                                        id="fp"
                                        name="fp"
                                        value={fpOptions.find(opt => opt.value === form.fp_type!)!}
                                        options={fpOptions}
                                        handleChange={handleSelectFp}
                                        isCreatable={false}
                                        isMulti={false}
                                        isRequired
                                        menuPortalTarget={document.getElementById('generate-vis-modal')}
                                    />
                                </ConditionalSelectField>
                            </div>

                            <div className="flex flex-col gap-6 justify-start items-start">
                                <p className="font-semibold text-default">Visualization Methods</p>
                                {form.visualization_method_specification_list!.map((method,i) => (
                                    <div className="flex justify-start items-start gap-4 w-full pb-6 border-b border-light-outline">
                                        {i > 0 && (
                                            <button id='remove-vis-method' onClick={(e:any) => removeVisMethod(e, i)} className="w-[24px] text-outline hover:text-primary transition-all">
                                                <DeleteIcon />
                                            </button>
                                        )}
                                        <div className="flex flex-col justify-start items-stretch gap-4 w-full grow">
                                            <div className="w-full">
                                                <ConditionalSelectField label="Visualization Method" showSelect={showSelect}>
                                                    <SelectField 
                                                        label="Visualization Method"
                                                        id="visMethod"
                                                        name="visMethod"
                                                        value={visMethodOptions.find(opt => opt.value === method.method_type)!}
                                                        options={visMethodOptions}
                                                        handleChange={handleSelectVisMethod}
                                                        isCreatable={false}
                                                        isMulti={false}
                                                        isRequired
                                                        menuPortalTarget={document.getElementById('generate-vis-modal')}
                                                        index={i}
                                                    />
                                                </ConditionalSelectField>
                                            </div>
                                            <div className="flex justify-between items-end gap-4">
                                                {method.method_type === 'tsne' ? (
                                                    <>
                                                    <div className="w-1/2">
                                                        <InputField 
                                                            label="Perplexity"
                                                            type="text"
                                                            id='perplexity'
                                                            name='perplexity'
                                                            value={method.params!.perplexity as number}
                                                            isRequired
                                                            handleChange={handleInput}
                                                            index={i}
                                                        />
                                                    </div>
                                                    <div className="w-1/2">
                                                        <ConditionalSelectField label="Method" showSelect={showSelect}>
                                                            <SelectField 
                                                                label="Method"
                                                                id="method"
                                                                name="method"
                                                                value={tsneOptions.find(opt => opt.value === method.params!.method as string)!}
                                                                options={tsneOptions}
                                                                handleChange={handleSelect}
                                                                isCreatable={false}
                                                                isMulti={false}
                                                                isRequired
                                                                menuPortalTarget={document.getElementById('generate-vis-modal')}
                                                                index={i}
                                                            />
                                                        </ConditionalSelectField>
                                                    </div>
                                                    </>
                                                ) : method.method_type === 'pca' ? (
                                                    <>
                                                    <div className="w-1/2">
                                                        <BooleanField 
                                                            label="Whiten"
                                                            id="whiten"
                                                            name="whiten"
                                                            index={i}
                                                            value={method.params!.whiten as boolean}
                                                            isRequired
                                                            handleChange={handleCheck}
                                                        />
                                                    </div>
                                                    <div className="w-1/2">
                                                        <ConditionalSelectField label="SVD Solver" showSelect={showSelect}>
                                                            <SelectField 
                                                                label="SVD Solver"
                                                                id="svd_solver"
                                                                name="svd_solver"
                                                                value={pcaOptions.find(opt => opt.value === method.params!.svd_solver as string)!}
                                                                options={pcaOptions}
                                                                handleChange={handleSelect}
                                                                isCreatable={false}
                                                                isMulti={false}
                                                                isRequired
                                                                menuPortalTarget={document.getElementById('generate-vis-modal')}
                                                                index={i}
                                                            />
                                                        </ConditionalSelectField>
                                                    </div>
                                                    </>
                                                ) : (
                                                    <>
                                                    <div className="w-1/3">
                                                        <InputField 
                                                            label="Nearest Neighbors"
                                                            type="text"
                                                            id='n_neighbors'
                                                            name='n_neighbors'
                                                            value={method.params!.n_neighbors as number}
                                                            isRequired
                                                            handleChange={handleInput}
                                                            index={i}
                                                        />
                                                    </div>
                                                    <div className="w-1/3">
                                                        <ConditionalSelectField label="Metric" showSelect={showSelect}>
                                                            <SelectField 
                                                                label="Metric"
                                                                id="metric"
                                                                name="metric"
                                                                value={umapMetricOptions.find(opt => opt.value === method.params!.metric as string)!}
                                                                options={umapMetricOptions}
                                                                handleChange={handleSelect}
                                                                isCreatable={false}
                                                                isMulti={false}
                                                                isRequired
                                                                menuPortalTarget={document.getElementById('generate-vis-modal')}
                                                                index={i}
                                                            />
                                                        </ConditionalSelectField>
                                                    </div>
                                                    <div className="w-1/3">
                                                        <ConditionalSelectField label="Init" showSelect={showSelect}>
                                                            <SelectField 
                                                                label="Init"
                                                                id="init"
                                                                name="init"
                                                                value={umapInitOptions.find(opt => opt.value === method.params!.init as string)!}
                                                                options={umapInitOptions}
                                                                handleChange={handleSelect}
                                                                isCreatable={false}
                                                                isMulti={false}
                                                                isRequired
                                                                menuPortalTarget={document.getElementById('generate-vis-modal')}
                                                                index={i}
                                                            />
                                                        </ConditionalSelectField>
                                                    </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <Buttons
                                    id='add-new-vis-method-btn'
                                    type='secondary'
                                    styling="solid"
                                    label='Add Visualization Method'
                                    startingAdornment={<PlusIcon/>}
                                    action={addVisMethod}
                                />
                            </div>
                            
                        </div>
                        <PopupFooter hasOutline={true} buttons={formBtns}/>
                    </div>
                </div>
                ) : (
                    <SuccessMessageModal 
                        handleClose={handleClose}
                        message="Your visualization is being generated. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in this page."
                    />
                )}
            </ModalBase>
        </div>
    )
}